import { useMemo, useState } from "react";
import {
  ALL_QUESTIONS,
  filterQuestionsByCareerStage,
  type Question,
} from "@/lib/questions";
import type { CareerStageValue } from "@/lib/guestCareerStage";
import { JOB_TYPES } from "@/lib/jobTypes";
import type { UniversityLinkQuestion } from "@/lib/universityLink/types";
import {
  filterUniversityQuestions,
  universityCategoryOptions,
  type UniversitySelectable,
} from "@/lib/questionSelect/selectableQuestion";

interface UseQuestionFiltersOptions {
  initialJob?: string;
  initialCategory?: string;
  // プロフィールの新卒/中途区分。null（未ログイン未選択・区分読込中・取得失敗）のときは
  // 区分絞り込みを行わず全質問を対象にする（後方互換）。
  careerStage?: CareerStageValue | null;
  // 大学が登録した練習用質問（#1861）。名簿に一致する学生だけが持つ。無ければ「大学専用」の行は出ない。
  universityQuestions?: ReadonlyArray<UniversityLinkQuestion>;
  // 「大学専用」の行の初期値（URL の ?university=）。null は未選択（通常の質問と一緒に表示）。
  initialUniversity?: string | null;
}

interface FilterOption<V extends string> {
  value: V;
  label: string;
}

interface UseQuestionFiltersResult {
  jobFilter: string;
  setJobFilter: (value: string) => void;
  selectedCategory: string;
  setSelectedCategory: (value: string) => void;
  jobOptions: ReadonlyArray<FilterOption<string>>;
  categoryOptions: ReadonlyArray<FilterOption<string>>;
  /** 共通質問の絞り込み結果。「大学専用」の行を選んでいる間は空 */
  filtered: Question[];
  filtersActive: boolean;
  resetFilters: () => void;
  /** 「大学専用」の行の選択。null は未選択（既定） */
  universityCategory: string | null;
  setUniversityCategory: (value: string | null) => void;
  /** 「大学専用」の行の選択肢。大学質問が無ければ空 */
  universityOptions: ReadonlyArray<FilterOption<string>>;
  /**
   * 表示する大学質問。「大学専用」の行を選んでいればその絞り込み結果、未選択なら
   * 職種・カテゴリも未選択のときだけ全件（職種・カテゴリで絞ると出さない。属性を持たないため）
   */
  universityFiltered: UniversitySelectable[];
}

// フロント上のカテゴリ表示エイリアス。
// 質問データ（questions.ts）の `category` 値はそのまま保ち、フロントでのみ別カテゴリへ束ねる。
// ここに登録した値はフィルタチップ・質問カード両方で右辺の表示名に置き換わる。
const CATEGORY_ALIASES: Readonly<Record<string, string>> = {
  学業: "ガクチカ",
  課外活動: "ガクチカ",
};

// 質問の生カテゴリを UI 表示用カテゴリに正規化する。
// `useQuestionFilters` のフィルタ判定と、画面側のカードバッジ表示で共通して使う。
export function getDisplayCategory(rawCategory: string): string {
  return CATEGORY_ALIASES[rawCategory] ?? rawCategory;
}

// /practice/select・/interview/select・/scripts/new で共有する2軸フィルタ用フック。
// まず区分（新卒/中途）で基底集合を絞り、その基底集合に対して job / category の2軸 AND で絞り込む。
// 職種・カテゴリの選択肢も基底集合から算出するため、0件になる選択肢は現れない。
export function useQuestionFilters(
  options: UseQuestionFiltersOptions = {}
): UseQuestionFiltersResult {
  const careerStage = options.careerStage ?? null;
  const universityQuestions = options.universityQuestions ?? EMPTY_UNIVERSITY_QUESTIONS;
  const [jobFilter, setJobFilterState] = useState<string>(options.initialJob ?? "all");
  const [selectedCategory, setSelectedCategoryState] = useState<string>(
    options.initialCategory ?? "すべて"
  );
  const [universityCategory, setUniversityCategoryState] = useState<string | null>(
    options.initialUniversity ?? null
  );

  // 大学専用の行と職種・カテゴリの行は排他にする。大学質問は職種・カテゴリの属性を
  // 持たないため、両方を同時に効かせると絞り込みが効いていないように見える。
  const setJobFilter = (value: string) => {
    setJobFilterState(value);
    setUniversityCategoryState(null);
  };
  const setSelectedCategory = (value: string) => {
    setSelectedCategoryState(value);
    setUniversityCategoryState(null);
  };
  const setUniversityCategory = (value: string | null) => {
    setUniversityCategoryState(value);
    if (value !== null) {
      setJobFilterState("all");
      setSelectedCategoryState("すべて");
    }
  };

  // 区分で絞った基底集合。区分なしなら ALL_QUESTIONS（後方互換）。
  const basePool = useMemo<Question[]>(
    () => filterQuestionsByCareerStage(ALL_QUESTIONS, careerStage),
    [careerStage]
  );

  // 基底集合に含まれる職種キー。職種選択肢の算出と、無効な初期職種のフォールバック判定に使う。
  const jobKeysInPool = useMemo<Set<string>>(() => {
    const keys = new Set<string>();
    for (const q of basePool) {
      for (const k of q.job_type_keys) keys.add(k);
    }
    return keys;
  }, [basePool]);

  // URL の職種パラメータ等で初期化した職種が、区分解決後の基底集合に存在しない場合は
  // 「すべて」として扱う（例: 新卒ユーザーが ?job=engineer 付きURLで開いても0件で固まらないように）。
  // setState を使う副作用ではなく描画時に算出することで、余計な再レンダリングを避ける。
  const effectiveJobFilter =
    jobFilter !== "all" && !jobKeysInPool.has(jobFilter) ? "all" : jobFilter;

  const jobOptions = useMemo<ReadonlyArray<FilterOption<string>>>(() => {
    const sorted = JOB_TYPES.filter((j) => jobKeysInPool.has(j.key));
    return [
      { value: "all", label: "すべて" },
      ...sorted.map((j) => ({ value: j.key, label: j.label_ja })),
    ];
  }, [jobKeysInPool]);

  const categoryOptions = useMemo<ReadonlyArray<FilterOption<string>>>(() => {
    // 基底集合の category を表示用に正規化してから重複排除する。
    // 例: 「学業」「課外活動」は表示用には共に「ガクチカ」となり、選択肢には1つだけ現れる。
    const cats = Array.from(
      new Set(basePool.map((q) => getDisplayCategory(q.category)))
    );
    return [
      { value: "すべて", label: "すべて" },
      ...cats.map((c) => ({ value: c, label: c })),
    ];
  }, [basePool]);

  // URL 由来の大学カテゴリが選択肢に無い（質問が消えた等）ときは未選択として扱う。
  const universityOptions = useMemo(
    () => universityCategoryOptions(universityQuestions),
    [universityQuestions]
  );
  const effectiveUniversityCategory =
    universityCategory !== null &&
    !universityOptions.some((option) => option.value === universityCategory)
      ? null
      : universityCategory;

  const filtered = useMemo<Question[]>(() => {
    if (effectiveUniversityCategory !== null) return [];
    return basePool.filter((q) => {
      const jobMatch =
        effectiveJobFilter === "all" ||
        q.job_type_keys.includes(effectiveJobFilter);
      const categoryMatch =
        selectedCategory === "すべて" ||
        getDisplayCategory(q.category) === selectedCategory;
      return jobMatch && categoryMatch;
    });
  }, [basePool, effectiveJobFilter, selectedCategory, effectiveUniversityCategory]);

  const universityFiltered = useMemo<UniversitySelectable[]>(() => {
    if (effectiveUniversityCategory !== null) {
      return filterUniversityQuestions(universityQuestions, effectiveUniversityCategory);
    }
    if (effectiveJobFilter !== "all" || selectedCategory !== "すべて") return [];
    return filterUniversityQuestions(universityQuestions, null);
  }, [universityQuestions, effectiveUniversityCategory, effectiveJobFilter, selectedCategory]);

  const filtersActive =
    effectiveJobFilter !== "all" ||
    selectedCategory !== "すべて" ||
    effectiveUniversityCategory !== null;

  const resetFilters = () => {
    setJobFilterState("all");
    setSelectedCategoryState("すべて");
    setUniversityCategoryState(null);
  };

  return {
    jobFilter: effectiveJobFilter,
    setJobFilter,
    selectedCategory,
    setSelectedCategory,
    jobOptions,
    categoryOptions,
    filtered,
    filtersActive,
    resetFilters,
    universityCategory: effectiveUniversityCategory,
    setUniversityCategory,
    universityOptions,
    universityFiltered,
  };
}

const EMPTY_UNIVERSITY_QUESTIONS: ReadonlyArray<UniversityLinkQuestion> = [];
